import { Headline, ProductTileSkeleton, Text, TextLink } from '@oneaudi/unified-web-alpha-components';
import { useMemo, useState } from 'react';
import { PrepareStockCarPriceFootnote } from '~/app/compositions/content/calculation-info/components/stockcar-price-footnote';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { MATCH_ROWS, MATCHES_PER_ROW } from '~/app/models/constans';
import PriceFilterPopover from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/components/price-filter';
import ResultTile from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/components/result-tile';
import {
	MatchFilter,
	PriceFilter,
} from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/models/types';
import {
	StyledContent,
	StyledMoreLink,
} from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/styles/results-dialog-styled-content';
import { useStockCarDialog } from '~/app/pages/configurator/components/dialogs/stock-car-dialog';
import { GeoData } from '~/app/pages/configurator/components/dialogs/stock-car-dialog/models/types';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useFootnotes } from '~/components/footnotes';
import { PRICE_DISCLAIMER_REMARK } from '~/components/footnotes/constants';
import { SimilarCarsInput } from '~/graphql/__generated__/graphql';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useSimilarCarsQuery } from '~/graphql/hooks/use-configured-car-queries';

export default function CarMatchingResultsDialog({
	searchLocation,
	radius,
	geoData,
}: {
	searchLocation?: string;
	radius?: number;
	geoData?: GeoData;
}) {
	const { config } = useMarketConfig();
	const { showStockCarDialog } = useStockCarDialog(config.stockCarConfig.enabled);
	const { carIdentifier } = useConfigurator();
	const { addFootnote } = useFootnotes();
	const { i18n } = useI18n();

	const priceDefaultValue: PriceFilter = [
		config.stockCarConfig.priceFilter.min,
		config.stockCarConfig.priceFilter.max,
	];
	const precision = config.stockCarConfig.modelPrecision;
	const scoreDefaultValue = 0.75;
	const deliveryDefaultValue: string[] = [];
	const [matchFilter, setMatchFilter] = useState<MatchFilter>({
		price: priceDefaultValue,
		score: scoreDefaultValue,
		delivery: deliveryDefaultValue,
	});
	const setupMatchFilter = (value: Partial<MatchFilter>) => {
		setMatchFilter((prevFilter) => {
			return { ...prevFilter, ...value };
		});
	};

	const [priceRange, setPriceRange] = useState<PriceFilter>(priceDefaultValue);

	const modelPrecisionConfig = {
		codePrecision: precision?.codePrecision ?? 4,
		yearsOffset: {
			end: precision?.end ?? 1,
			start: precision?.start ?? -1,
		},
	};

	const similarCarsInput: SimilarCarsInput =
		geoData?.lat && geoData.lng && radius
			? {
					maxNumberOfCars: 50,
					geoSearch: { latitude: geoData.lat, longitude: geoData.lng, maxDistance: radius },
					modelPrecision: modelPrecisionConfig,
				}
			: {
					maxNumberOfCars: 50,
					modelPrecision: modelPrecisionConfig,
				};

	const { data: similarCarsData, loading: similarCarSearchLoading } = useSimilarCarsQuery(
		carIdentifier,
		config.financingInput,
		similarCarsInput,
	);

	const similarCars = similarCarsData?.configuredCar.similarCars?.newCars?.cars;

	const priceFilterIndicator = JSON.stringify(priceDefaultValue) !== JSON.stringify(matchFilter.price) ? '(1)' : '';

	const results = useMemo(() => {
		if (!similarCars || similarCars.length < 1) {
			return undefined;
		}

		// Filter out undefined cars
		const initialResults = similarCars.filter((car) => !!car.stockCar);

		// Filter by price range, if market has prices active - then filter by Score
		const resultsByPrice = initialResults

			.filter(({ stockCar }) => {
				if (!config.showPrices) {
					return true;
				}
				if (config.showPrices && (!stockCar.carPrices || stockCar.carPrices.length === 0)) {
					return;
				}
				if (config.showPrices && stockCar.carPrices && stockCar.carPrices.length > 0) {
					const price = stockCar.carPrices.find((d) => d.type === 'sale')?.price;
					if (!price) return;
					return matchFilter.price[0] < price.value && matchFilter.price[1] > price.value;
				}
			})
			.filter(({ score }) => score >= matchFilter.score);

		return resultsByPrice;
	}, [matchFilter.price, matchFilter.score, similarCars]);

	// Register footnotes
	// config.stockCarConfig.disclaimer ? addFootnote(STOCK_CAR_REMARK, i18n('ngc.stockcar.matches.disclaimer')) : '';
	config.stockCarConfig.pricedisclaimer
		? addFootnote(PRICE_DISCLAIMER_REMARK, PrepareStockCarPriceFootnote() || '')
		: '';

	const moreCarsThanTwo = !!results && results.length > MATCHES_PER_ROW * MATCH_ROWS;
	const [showMoreThanTwo, setShowMore] = useState(false);

	const handleLocationClick = () => {
		showStockCarDialog();
		appTrackingService.stockCarClickEvent({
			eventAction: EventAction.InternalLink,
			eventName: `configurator (stock car matching) - click to change location`,
			elementName: 'text link',
			label: `${searchLocation}, +${radius} km`,
		});
	};

	const handleShowMore = () => {
		setShowMore(true);
		appTrackingService.stockCarClickEvent({
			eventAction: EventAction.Content,
			eventName: "configurator (stock car matching) - click on 'show more'",
			label: i18n('ngc.stockcar.matches.more', {
				results: results ? results.length - MATCHES_PER_ROW * MATCH_ROWS : 0,
			}),
			elementName: 'text link',
		});
	};

	return (
		<StyledContent>
			{}
			<div tabIndex={0}>
				<Headline tag="h2" variant={{ type: 'headline', order: '3', weight: 'bold' }}>
					{i18n('ngc.stockcar.matches.title')}
				</Headline>
				{config.stockCarConfig.subheader && <Text>{i18n('ngc.stockcar.matches.subheader')}</Text>}
			</div>
			{config.stockCarConfig.geoFilter && (
				<TextLink
					href="#"
					icon="poi"
					iconPosition="leading"
					onClick={() => {
						handleLocationClick();
					}}
					size="md"
					variant="standalone">
					{`${searchLocation}, +${radius} km`}
				</TextLink>
			)}
			<div className="filters--container">
				{config.showPrices && (
					<PriceFilterPopover
						setPriceRange={setPriceRange}
						setupMatchFilter={setupMatchFilter}
						priceDefaultValue={priceDefaultValue}
						priceFilterIndicator={priceFilterIndicator}
						priceRange={priceRange}
						minPrice={config.stockCarConfig.priceFilter?.min ?? 70000}
						maxPrice={config.stockCarConfig.priceFilter?.max ?? 200000}
					/>
				)}
			</div>
			<div className="tile-container">
				{similarCarSearchLoading ? (
					<>
						<ProductTileSkeleton className="card-skeleton" />
						<ProductTileSkeleton className="card-skeleton" />
						<ProductTileSkeleton className="card-skeleton" />
						<ProductTileSkeleton className="card-skeleton" />
					</>
				) : !results || results?.length === 0 ? (
					<div className="not-found">
						<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
							{i18n('ngc.stockcar.matches.notfound.title')}
						</Text>
						<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
							{i18n('ngc.stockcar.matches.notfound.description')}
						</Text>
					</div>
				) : (
					<>
						{results
							.filter((_, index) => showMoreThanTwo || index < MATCHES_PER_ROW * MATCH_ROWS)
							.map((item, index) => (
								<ResultTile key={item.stockCar.id} similarCar={item} carIndex={index} />
							))}
						{moreCarsThanTwo && !showMoreThanTwo && (
							<StyledMoreLink>
								<TextLink size="md" variant={'standalone'} onClick={handleShowMore}>
									{i18n('ngc.stockcar.matches.more', {
										results: results.length - MATCHES_PER_ROW * MATCH_ROWS,
									})}
								</TextLink>
							</StyledMoreLink>
						)}
					</>
				)}
			</div>
		</StyledContent>
	);
}
